import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore } from "@/global/store";
import { selectInventoryFilters } from "@/global/store/data/gameFilters";
import { cartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import {
  eventsState,
  selectEvents,
  selectEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { EventType, InventoryType } from "@/global/store/data/types";
import { useDispatch } from "react-redux";
import { useFilterApis } from "../../GameFilters/useFilterApis";
import { inventoriesState } from "@/global/store/data/purchaseSeats/inventories";
import { seatMapState } from "@/global/store/data/seatMap";
import { packagesState } from "@/global/store/data/purchaseSeats/packages";
import { selectUser } from "@/global/store/data/user";
import { authOriginUIDs, authUIDs } from "@/global/config/constants";
import { routes } from "@/global/routes";
import { isAuthenticatingState } from "@/global/store/data/isAuthenticating";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useSelectActiveEvent } from "./useSelectActiveEvent";
import { useCartHelpers } from "../useCartHelpers";
import { useApiRoutes } from "@/global/hooks/useApiRoutes";
import { createGetInvetoryApiPayload } from "@/global/api/api.utils";
import { findEventByEventCode } from "../utils.cart";
import { getEventsAboveMaxLimit } from "../utils.eventValidators";
import { useMaxLimitErrorModal } from "../modals/MaxLimitErrorModal";
import { calcDefaultPurchaseSeatTab } from "../utils.purchaseSeats";
import { toast } from "react-toastify";
import { parseApiErrorMessage } from "@/global/utils/utils";

export function useChooseSeats() {
  const dispatch = useDispatch();
  const router = useRouterExtended();
  const teamConfig = useTeamConfig();
  const { teamSchema } = teamConfig;

  // apis
  const { getEventsApi, getPackagesApi, getInventoryApi } = useApiRoutes();
  const { handleGetInventoryFilters } = useFilterApis();

  // states
  const events = useAppStore(selectEvents);
  const selectedEvents = useAppStore(selectEventsSelected);
  const urlEventCodes = router?.query?.eventCodes as string;
  const urlPackageId = Number(router.query.packageId) as number;
  const { activeEvent, urlActiveEventCode } = useSelectActiveEvent();
  const inventoryFilters = useAppStore(selectInventoryFilters(teamConfig));
  const user = useAppStore(selectUser);
  const maxLimitErrorModal = useMaxLimitErrorModal();

  // helpers
  const {
    setCartActiveEventUrl,
    handleNoInventoryEvent,
    switchPurchaseSeatTab,
  } = useCartHelpers();

  // handlers
  const handleInventorySelect = (
    inv: InventoryType,
    event?: EventType | null
  ) => {
    const inventoryEvent = event || activeEvent;
    if (inventoryEvent) {
      dispatch(
        cartManagerState.actions.updateProducts({
          product: inv,
          eventCode: inventoryEvent?.eventCode,
        })
      );
    }
  };

  const handleInventoryMouseOver = (inv: InventoryType) => {
    dispatch(
      seatMapState.actions.set({
        hoveredInventorySection: "S_" + inv.sectionName,
      })
    );
  };

  const handleInventoryMouseLeave = (inv: InventoryType) => {
    // handle mouse leave here if truly necessary
  };

  const handleGetPackages = async () => {
    if (!user || !urlPackageId) return;
    // getting event packages if package id is in url
    let payload = { teamSchema, userId: user?.userId };
    const { data, error } = await getPackagesApi.post(payload);
    if (error) return toast.error(parseApiErrorMessage(error));

    dispatch(packagesState.actions.setPackages(data));

    if (data) {
      let selected = data.find((pkg) => pkg?.id == urlPackageId);
      if (selected) {
        dispatch(packagesState.actions.setPackageSelected(selected));
      }
    }
  };

  const handleLoadEvents = async () => {
    let eventsFetched: EventType[] = events;
    let eventsSelected: EventType[] = selectedEvents;
    let eventToActive: EventType | null = activeEvent;

    // if no user, returning with whatever values are available
    if (!user) return { eventsFetched, eventsSelected, eventToActive };

    // fetching events if no events in redux is empty
    if (!eventsFetched.length) {
      const { data, error } = await getEventsApi.post({
        teamSchema,
        eventFilters: {
          dates: [],
          opponentNames: [],
          eventCodes: [],
          categories: [],
        },
        userId: user?.userId,
      });
      if (error) {
        toast.error(parseApiErrorMessage(error));
        return { eventsFetched, eventsSelected, eventToActive };
      }
      eventsFetched = data;
      dispatch(eventsState.actions.setEvents(eventsFetched));
    }

    // setting event selection if event codes are in url and no events are selected in redux
    if (urlEventCodes && !eventsSelected.length) {
      const urlEventCodesArr = urlEventCodes.split("-");
      eventsSelected = eventsFetched.filter((evt) => {
        return urlEventCodesArr.includes(evt.eventCode);
      });
      dispatch(eventsState.actions.setEventsSelected(eventsSelected));
    }

    // setting active event if active event code is in url
    if (!activeEvent) {
      if (urlActiveEventCode && eventsSelected.length) {
        eventToActive = findEventByEventCode(eventsFetched, urlActiveEventCode);
      } else if (!urlActiveEventCode && eventsSelected.length) {
        eventToActive = eventsSelected[0];
      } else if (urlActiveEventCode && !eventsSelected.length) {
        // handling the direct event code url purchase based on team config
        const isDirectEventCodeAllowed = teamConfig?.purchaseSeatConfigs?.isDirectEventCodeUrlPurchaseAllowed // prettier-ignore
        if (!isDirectEventCodeAllowed) {
          eventToActive = null;
          const defaultTab = calcDefaultPurchaseSeatTab(teamConfig);
          await switchPurchaseSeatTab(defaultTab);
        }
      }
      if (eventToActive) setCartActiveEventUrl(eventToActive);
    }

    // handle max limit event error
    const maxLimitEvents = getEventsAboveMaxLimit(eventsSelected, teamConfig);
    if (maxLimitEvents.length) maxLimitErrorModal.open({ maxLimitEvents });

    return {
      eventsFetched,
      eventsSelected,
      eventToActive,
    };
  };

  const handleLoadInventories = async () => {
    if (!activeEvent) return;

    const payload = createGetInvetoryApiPayload({
      activeEvent,
      teamConfig,
      inventoryFilters,
    });

    let { data: inventories, error } = await getInventoryApi.post(payload);
    if (error) return toast.error(parseApiErrorMessage(error));

    if (!inventories?.length) {
      await handleNoInventoryEvent({
        event: activeEvent,
        isInventoryApiTriggered: true,
      });
    }

    dispatch(
      inventoriesState.actions.set({
        inventories: inventories || [],
        eventCode: activeEvent.eventCode,
      })
    );
  };

  const handleAuthWithEventCodes = async () => {
    if (urlEventCodes && !user) {
      if (teamConfig?.authConfigs?.isChooseSeatsAuthWithEventCodesDisabled) {
        return await router.pushSchema(teamConfig?.defaultRoute);
      }

      dispatch(isAuthenticatingState.actions.set(true));
      let state: any = {
        authOrigin: authOriginUIDs.purchaseSeats,
        pathname: routes.purchaseSeats,
        query: router.query,
      };
      state = window.btoa(JSON.stringify(state)).replaceAll("=", "");
      await router.push({
        pathname: routes.auth,
        query: { authType: authUIDs.login, teamSchema, state },
      });
    }
  };

  return {
    handleInventorySelect,
    handleLoadInventories,
    handleInventoryMouseOver,
    handleInventoryMouseLeave,
    handleLoadEvents,
    handleGetPackages,
    handleAuthWithEventCodes,
    handleGetInventoryFilters,
  };
}
